// @ts-nocheck
import React, { useState, useEffect } from "react";
import ProcessedScatterGraph from "../forecastGraphs/ProcessedScatterGraph";
import { SkeletonChart } from "../Skeleton";
import {
  formatProcessedDataGraph,
  separatePercentileChannels,
} from "../../util/utils";
import {
  fetchAndParseForecastRecord,
  getVariationOptions,
} from "../../utils/modelOutputUtils";
import { pickModelKey } from "../../utils/seriesMetaData";
import { applyChannelDisplay } from "../../utils/composedForecastDisplay";
import type { SourceDisplaySettings } from "../../utils/composedForecastDisplay";

import Select from "../ReusableComponents/Select";
interface ForecastRecordPreviewProps {
  s3Key: string;
  /** Show this variable and hide the preview's own Variation picker (the caller has one, e.g. a Custom View
   *  source's Variable). Unset: the preview offers every variable itself. */
  variation?: string;
  /** A Custom View source's display settings: the preview hides and colours channels the way the view draws them. */
  display?: SourceDisplaySettings | null;
  /** Colour of the main line when the display picks none (a Custom View's main forecast is the brand olive). */
  meanColor?: string | null;
}

const ForecastRecordPreview: React.FC<ForecastRecordPreviewProps> = ({
  s3Key,
  variation,
  display = null,
  meanColor = null,
}) => {
  const controlled = variation !== undefined;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{
    otherChannels: any;
    percentiles: any;
  } | null>(null);
  const [yUnit, setYUnit] = useState<string>("");
  const [variationOptions, setVariationOptions] = useState<
    { key: string; label: string }[]
  >([]);
  const [selectedVariation, setSelectedVariation] = useState<string>("");
  const [parsedData, setParsedData] = useState<any>(null);

  useEffect(() => {
    loadPreview(s3Key);
  }, [s3Key]);

  const loadPreview = async (key: string) => {
    setLoading(true);
    setError(null);
    setGraphData(null);

    try {
      const { parsedData: data } = await fetchAndParseForecastRecord(key);
      setParsedData(data);

      const options = getVariationOptions(data);
      setVariationOptions(options);
      const initial = pickModelKey(data?.models, variation);
      setSelectedVariation(initial);

      applyVariation(data, initial);
    } catch (err: any) {
      console.error("Error loading forecast preview:", err);
      setError(err?.message || "Failed to load forecast data from S3");
    } finally {
      setLoading(false);
    }
  };

  const applyVariation = (data: any, variationKey: string) => {
    const models = data?.models || {};
    const modelData = models[variationKey];
    if (!modelData?.data) {
      setError("No data found for this variation");
      setGraphData(null);
      return;
    }

    const formatted = formatProcessedDataGraph(modelData.data);
    const { otherChannels, percentiles } = separatePercentileChannels(formatted);
    setGraphData({ otherChannels, percentiles });
    setYUnit(modelData.yUnit || "");
  };

  // A caller-chosen variable follows the caller, including a change made while the file was still loading
  useEffect(() => {
    if (!controlled || !parsedData) return;
    const key = pickModelKey(parsedData?.models, variation);
    if (key === selectedVariation) return;
    setSelectedVariation(key);
    applyVariation(parsedData, key);
  }, [variation, parsedData]);

  const handleVariationChange = (variationKey: string) => {
    setSelectedVariation(variationKey);
    if (parsedData) {
      applyVariation(parsedData, variationKey);
    }
  };

  if (loading) {
    return (
      <SkeletonChart bars={18} label="Loading forecast preview" withHeader={false} />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="font-inter text-eai-body-sm text-eai-danger-text">{error}</p>
      </div>
    );
  }

  if (!graphData) return null;

  // Unset display = the file as it is, with the graph's own colours
  const shown = display ? applyChannelDisplay(graphData.percentiles, graphData.otherChannels, display) : null;

  return (
    <div className="space-y-3">
      {/* Variation selector */}
      {!controlled && variationOptions.length > 1 && (
        <div className="flex items-center gap-2">
          <label className="eai-meta-label">
            Variation:
          </label>
          <Select
            value={selectedVariation}
            onChange={(e) => handleVariationChange(e.target.value)}
            size="sm"
          >
            {variationOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* Chart */}
      <div className="h-[350px] w-full">
        <ProcessedScatterGraph
          percentiles={shown ? shown.percentiles : graphData.percentiles}
          otherChannels={shown ? shown.otherChannels : graphData.otherChannels}
          channelColors={shown?.channelColors ?? null}
          bandColors={shown?.bandColors ?? null}
          meanColor={meanColor}
          labels={[]}
          yaxis={yUnit}
          timeZone={"UTC"}
          assetTimeZone={"UTC"}
          timeZoneMode={"utc"}
          title={
            variationOptions.find((v) => v.key === selectedVariation)?.label ||
            "Forecast Preview"
          }
          hideGraphControls
          hideMobileControls
        />
      </div>
    </div>
  );
};

export default ForecastRecordPreview;
